import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { autoCurrentEvent, deviceToday, type SyncOp, type SyncOpResult } from '@wr/shared';
import { get, post, putFile } from '../api.js';
import { useSignedIn } from '../session.js';
import { keepPasswordCheck, keptKioskPin, refreshKioskPin, reportKioskExits } from './kioskPin.js';
import { refreshMyCard } from './myCard.js';
import { openStore } from './local.js';
import { RECEIPT_PREFIX } from './store.js';
import type { LocalStore } from './store.js';
import {
  runSync,
  type DataReset,
  type PullPage,
  type SyncOutcome,
  type SyncTransport,
} from './sync.js';
import { keptItems, reconcile, type OfflineAsset } from './media.js';

/**
 * The signed-in person's local data and the sending of their changes.
 * Sync runs at start, when the network returns, every 30 seconds, after
 * each saved change, and on "Send now".
 */
export interface OfflineValue {
  /** null while opening, or when this browser keeps nothing (online only). */
  store: LocalStore | null;
  /** This browser keeps nothing on the device (some private windows). */
  unavailable: boolean;
  counts: { pending: number; rejected: number };
  sending: boolean;
  /** The last sync attempt's result, null before the first one. */
  outcome: SyncOutcome | null;
  /** Saves changes on the device first (under 100 ms), in order, then sends them. */
  save(...ops: SyncOp[]): Promise<void>;
  /** A scanned card or a receipt: its photo and its changes, kept here first, then sent. */
  saveScan(photo: Blob, ...ops: SyncOp[]): Promise<void>;
  /** The current event on this device (chosen at step 6), null at the office. */
  currentEventId(): Promise<string | null>;
  /** The same, for screens: undefined while the device storage opens. */
  eventId: string | null | undefined;
  /** Picks the current event of this device (null: no event); its records are pulled. */
  setEventId(id: string | null): Promise<void>;
  /** Sends every waiting change at once, without waiting for the next try. */
  sendNow(opId?: string): Promise<void>;
}

const SYNC_EVERY_MS = 30_000;

export const transport: SyncTransport = {
  push: (ops, resetId) =>
    post<{ results: SyncOpResult[]; reset?: DataReset | null }>('/sync/push', { ops, resetId }),
  pull: (cursor, eventId) =>
    get<PullPage>(
      `/sync/pull?cursor=${cursor}${eventId ? `&eventId=${encodeURIComponent(eventId)}` : ''}`,
    ),
  // A receipt's photo is kept under "expense:<id>"; a scanned card under its contact.
  upload: (key, photo) =>
    key.startsWith(RECEIPT_PREFIX)
      ? putFile(`/expenses/${key.slice(RECEIPT_PREFIX.length)}/receipt`, photo)
      : putFile(`/capture/scans/${key}/card`, photo),
};

/**
 * The show of today whose team the person is on becomes the current event
 * of this device (decision of Nov 3, 2026), when it has none and did not
 * get this one already. True when it changed (its records are pulled next).
 */
async function pickShowByItself(store: LocalStore): Promise<boolean> {
  const show = autoCurrentEvent(await store.listRecords('event'), deviceToday());
  if (!show) return false;
  const current = (await store.getMeta<string>('eventId')) ?? null;
  if (current || (await store.getMeta<string>(AUTO_PICKED)) === show.id) return false;
  await store.setMeta('eventId', show.id);
  await store.setMeta(AUTO_PICKED, show.id);
  return true;
}

/** The show already picked by itself on this device. */
const AUTO_PICKED = 'eventAutoPicked';

const OfflineContext = createContext<OfflineValue | null>(null);

export function OfflineProvider({ children }: { children: ReactNode }) {
  const { user, refresh, can } = useSignedIn();
  const handsOver = can('contacts.handoff') || can('kiosk.start');
  const pinFetchedAt = useRef(0);
  // My business card, kept for shows without network: once per connection.
  const cardFetched = useRef(false);
  const contentChecked = useRef(0);
  const canReadContent = can('content.read');
  const [store, setStore] = useState<LocalStore | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [counts, setCounts] = useState({ pending: 0, rejected: 0 });
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<SyncOutcome | null>(null);
  const [eventId, setEventIdState] = useState<string | null | undefined>(undefined);
  const running = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  // A sync still running when the person signs out (or a test ends) changes no state.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    openStore(user.id).then(
      (opened) => {
        // The password's hash from the sign-in, for leaving the kiosk without network.
        if (handsOver) void keepPasswordCheck(opened, user.email).catch(() => undefined);
        if (current) setStore(opened);
      },
      () => current && setUnavailable(true),
    );
    return () => {
      current = false;
    };
  }, [user.id, user.email, handsOver]);

  const sync = useCallback(async () => {
    // One run at a time; a request during a run starts one more after it.
    if (running.current) {
      again.current = true;
      return running.current;
    }
    const run = (async () => {
      do {
        again.current = false;
        try {
          // The store may still be opening (a change saved right at start).
          const opened = await openStore(user.id);
          const pending = (await opened.counts()).pending > 0;
          if (mounted.current) setSending(pending);
          const result = await runSync(opened, transport);
          if (!mounted.current) return;
          setOutcome(result);
          // The kiosk PIN's hash, kept for shows without network: at the first sync of
          // each connection, every minute while the device has none, every 10 minutes
          // otherwise (round 2: a PIN set in Settings arrives without reloading).
          // Round 2: the library's rules for the content kept here, at most every 10 minutes
          // (an item deleted leaves; a new version is only noted, never downloaded by itself).
          if (
            result === 'done' &&
            canReadContent &&
            Date.now() - contentChecked.current > 600_000
          ) {
            contentChecked.current = Date.now();
            if (Object.keys(await keptItems(opened)).length > 0) {
              await get<{ items: OfflineAsset[] }>('/content')
                .then(({ items }) => reconcile(opened, user.id, items))
                .catch(() => undefined);
            }
          }
          if (result === 'done' && !cardFetched.current) {
            cardFetched.current = true;
            await refreshMyCard(opened);
          }
          if (result === 'done' && handsOver) {
            const since = Date.now() - pinFetchedAt.current;
            if (since > 600_000 || (since > 60_000 && !(await keptKioskPin(opened)))) {
              pinFetchedAt.current = Date.now();
              await refreshKioskPin(opened);
            }
            await reportKioskExits(opened);
          }
          // During a show of the person's team, its event becomes the current one by
          // itself, once (Nov 3, 2026): a seller who then picks another, or none, keeps it.
          if (result === 'done' && (await pickShowByItself(opened))) again.current = true;
          const counted = await opened.counts();
          // A data reset (round 2) may have taken the current event away.
          const current = (await opened.getMeta<string>('eventId')) ?? null;
          if (!mounted.current) return;
          setCounts(counted);
          setEventIdState((was) => (was === undefined || was === current ? was : current));
          if (result === 'needs_sign_in') void refresh();
        } catch {
          // The device storage failed (closed, full): the changes stay where
          // they are, and the next run tries again.
        }
      } while (again.current);
    })().finally(() => {
      running.current = null;
      if (mounted.current) setSending(false);
    });
    running.current = run;
    return run;
  }, [user.id, refresh, handsOver, canReadContent]);

  useEffect(() => {
    if (!store) return;
    let current = true;
    void store.getMeta<string>('eventId').then(
      (id) => current && setEventIdState(id ?? null),
      () => current && setEventIdState(null),
    );
    return () => {
      current = false;
    };
  }, [store]);

  useEffect(() => {
    if (!store) return;
    void store.counts().then(
      (c) => mounted.current && setCounts(c),
      () => undefined,
    );
    void sync();
    // The network is back: everything waiting goes at once (brief section 2, rule 10).
    const onOnline = () => {
      // A new connection: the kiosk PIN is fetched again at its first sync.
      pinFetchedAt.current = 0;
      cardFetched.current = false;
      void store.makeDue().then(sync);
    };
    const onTimer = () => void sync();
    window.addEventListener('online', onOnline);
    const timer = setInterval(onTimer, SYNC_EVERY_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      clearInterval(timer);
    };
  }, [store, sync]);

  const save = useCallback(
    async (...ops: SyncOp[]) => {
      // A change made right after the app opens waits for the store, never fails.
      const opened = store ?? (await openStore(user.id));
      for (const op of ops) await opened.enqueue(op);
      // Saved on the device: the screen goes on at once. The count of what
      // waits and the sending follow, and never make a saved change look
      // failed (round 2: Create the lead sometimes waited on them).
      void opened.counts().then(
        (counted) => mounted.current && setCounts(counted),
        () => undefined,
      );
      void sync();
    },
    [store, sync, user.id],
  );

  const saveScan = useCallback(
    async (photo: Blob, ...ops: SyncOp[]) => {
      const opened = store ?? (await openStore(user.id));
      const record = ops.find(
        (op) => (op.entity === 'contact' || op.entity === 'expense') && op.action === 'upsert',
      );
      if (!record || record.entity === 'lead_contact') throw new Error('No contact');
      // A receipt's photo is kept under its own prefix (step 8).
      const contact = {
        id: record.entity === 'expense' ? `${RECEIPT_PREFIX}${record.id}` : record.id,
      };
      // The photo first, held: if the device is full, nothing half-saved
      // remains, and a sync running meanwhile never sends it before its contact.
      await opened.savePhoto(contact.id, photo, Date.now(), true);
      try {
        for (const op of ops) await opened.enqueue(op);
      } catch (error) {
        await opened.removePhoto(contact.id).catch(() => undefined);
        throw error;
      }
      await opened.releasePhoto(contact.id);
      // Saved: as save(), the count and the sending follow without holding it.
      void opened.counts().then(
        (counted) => mounted.current && setCounts(counted),
        () => undefined,
      );
      void sync();
    },
    [store, sync, user.id],
  );

  const currentEventId = useCallback(async () => {
    try {
      const opened = store ?? (await openStore(user.id));
      return (await opened.getMeta<string>('eventId')) ?? null;
    } catch {
      return null;
    }
  }, [store, user.id]);

  const setEventId = useCallback(
    async (id: string | null) => {
      const opened = store ?? (await openStore(user.id));
      await opened.setMeta('eventId', id);
      if (mounted.current) setEventIdState(id);
      // The records of the new event come with the next pull.
      void sync();
    },
    [store, sync, user.id],
  );

  const sendNow = useCallback(
    async (opId?: string) => {
      if (!store) return;
      await store.makeDue(opId);
      const counted = await store.counts();
      if (mounted.current) setCounts(counted);
      await sync();
    },
    [store, sync],
  );

  return (
    <OfflineContext.Provider
      value={{
        store,
        unavailable,
        counts,
        sending,
        outcome,
        save,
        saveScan,
        currentEventId,
        // A browser that keeps nothing has no current event.
        eventId: unavailable ? null : eventId,
        setEventId,
        sendNow,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline(): OfflineValue {
  const value = useContext(OfflineContext);
  if (!value) throw new Error('useOffline outside OfflineProvider');
  return value;
}
